import type {
  ConnectorStandard,
  CurrentType,
  OperatorSlug,
  PaymentMethod,
  PointAvailability,
  VoltageClass,
} from '@ev/shared'

/**
 * The filter facets the map exposes.
 *
 * An empty array means "no constraint on this facet", never "match nothing" -
 * so {@link EMPTY_FILTERS} is the unfiltered view and the URL for it is empty.
 */
export interface Filters {
  readonly operators: readonly OperatorSlug[]
  /**
   * Networks the registry does not track, held as folded names rather than slugs.
   *
   * `operators: ['other']` selects the whole long tail - several hundred hotels,
   * councils and car dealers along with every real network too small to have a
   * registry entry. This facet is how one of those networks is picked out of it:
   * the values are {@link operatorKey} outputs, so `Threeforce B.V.` and
   * `THREEFORCE BV` are the same filter. It ORs with `operators` rather than
   * ANDing - both answer "whose charger", so ticking Tauron and ThreeForce shows
   * both, exactly as ticking two brands does.
   */
  readonly operatorNames: readonly string[]
  readonly connectors: readonly ConnectorStandard[]
  readonly currentTypes: readonly CurrentType[]
  /** Minimum connector power in kW; `null` for no constraint. */
  readonly minPowerKw: number | null
  readonly voltageClasses: readonly VoltageClass[]
  readonly availability: readonly PointAvailability[]
  readonly paymentMethods: readonly PaymentMethod[]
}

export const EMPTY_FILTERS: Filters = {
  operators: [],
  operatorNames: [],
  connectors: [],
  currentTypes: [],
  minPowerKw: null,
  voltageClasses: [],
  availability: [],
  paymentMethods: [],
}

/** Every array-valued facet, for generic toggle handling in the filter UI. */
export const FILTER_FACETS = [
  'operators',
  'operatorNames',
  'connectors',
  'currentTypes',
  'voltageClasses',
  'availability',
  'paymentMethods',
] as const

export type FilterFacet = (typeof FILTER_FACETS)[number]

/** Value type stored in a given array facet. */
export type FilterFacetValue<F extends FilterFacet> = Filters[F][number]

export const isFiltersEmpty = (filters: Filters): boolean =>
  filters.minPowerKw === null && FILTER_FACETS.every((facet) => filters[facet].length === 0)

/** Add a value if absent, remove it if present. Order is preserved. */
export const toggleFacetValue = <F extends FilterFacet>(
  filters: Filters,
  facet: F,
  value: FilterFacetValue<F>,
): Filters => {
  const current = filters[facet] as readonly FilterFacetValue<F>[]
  const next = current.includes(value)
    ? current.filter((entry) => entry !== value)
    : [...current, value]
  return { ...filters, [facet]: next }
}
